/**
 * Attendance Configuration Types
 * Transport layer and domain models for attendance configuration
 */

// ─── Transport layer DTOs ───────────────────────────────────────────────────

/** Configuration object inside data.configuration */
export interface LC_AttendanceConfigurationOut {
  // --- General: Marcajes ---
  offlineGeoLocationRequired: boolean | null;
  lunchBreakMarks: boolean | null;
  validateGeoLocation: boolean | null;
  disableGeoMark: boolean | null;
  notifyGeoInconsistencyApi: boolean | null;
  enableMarkRejection: boolean | null;
  markRejectionDeadlineHours: number | null;
  enableTodayMarkModification: boolean | null;

  // --- General: Eventos ---
  eventDefinitionMethod: string | null;
  shiftChangeEntryEventId: number | null;
  shiftChangeExitEventId: number | null;
  lunchBreakToleranceMinutes: number | null;
  lunchBreakEntryEventId: number | null;
  lunchBreakExitEventId: number | null;

  // --- Cálculo: Cortes Mensuales ---
  monthlyCut: boolean | null;
  sendConceptDetail: boolean | null;

  // --- Cálculo: Condiciones ---
  holidayAsWorkday: boolean | null;
  holidayAsWorkdayArt38: boolean | null;
  halfDayAbsenteeismType: number | null;

  // --- Notificaciones: Tolerancia ---
  entryMarkToleranceMinutes: number | null;
  exitMarkToleranceMinutes: number | null;
  duplicateRecordToleranceMinutes: number | null;
  absenceByApplication: string | null;

  // --- Notificaciones: Notificaciones ---
  notifyEmployeeDelay: boolean | null;
  notifyManagerDelay: boolean | null;
  notifyAccessControl: boolean | null;
  notifyManagementInsistence: boolean | null;
  managementInsistenceEmail: string | null;
  notifyDisconnectedDevice: boolean | null;
  disconnectedDeviceEmail: string | null;
  incidentNotificationEmployeeId: number | null;
  notifyEntryDelay: boolean | null;
  entryDelayNotificationTolerance: string | null;
  notifyExitDelay: boolean | null;
  exitDelayNotificationTolerance: string | null;
  contractCompanyAbsence: boolean | null;
  deliveryAlertEmailTo: string | null;
  dtAuditStartNotificationEmails: string | null;

  // --- Validaciones: Gestor de Marcas ---
  autoFillHolidays: boolean | null;
  autoFillVacations: boolean | null;
  autoFillSickLeave: boolean | null;
  autoFillSpecialPermissions: boolean | null;
  autoFillAdministrativePermissions: boolean | null;
  autoFillFractionPermissions: boolean | null;

  // --- Validaciones: Horas Extras ---
  overtimeApprovalFlowEnabled: boolean | null;

  // --- Validaciones: Pacto Compensación ---
  autoCompensationMode: string | null;
  cutToleranceDays: number | null;

  // --- Notificaciones adicionales ---
  notifyExcessScheduleEmail: string | null;
}

/** Item from data.attendanceMarkParameters */
export interface LC_AttendanceMarkParameter {
  id: number;
  attributeName: string;
  value: string | null;
}

/** Item from data.overtimeApprovalFlowRoles */
export interface LC_OvertimeApprovalFlowRole {
  flowProfileRoleId: number;
  flowProfileId: number;
  roleId: number;
  profileCode: string;
}

/** Wrapper for the full GET response (data envelope already unwrapped by apiClient) */
export interface LC_AttendanceConfigurationResponse {
  configuration: LC_AttendanceConfigurationOut;
  attendanceMarkParameters: LC_AttendanceMarkParameter[];
  overtimeApprovalFlowRoles: LC_OvertimeApprovalFlowRole[];
}

// ─── Mark parameter attribute names ─────────────────────────────────────────

export const ATTENDANCE_MARK_PARAM = {
  GROUP_CLOSURE: 'BloqueoPorGrupo',
  CALENDAR_SCHEDULE_MANAGEMENT: 'ObtenerHorarioDoble',
  CALCULATION_BY_CONTRACT_END: 'CalcByFechaContrato',
} as const;

// ─── Overtime approval flow profile codes ───────────────────────────────────

export const OVERTIME_FLOW_PROFILE_CODE = {
  REVIEWER: 'REVISOR',
  APPROVER: 'APROBADOR',
} as const;

// ─── Update transport layer DTOs ────────────────────────────────────────────

/** Configuration fields for PUT /attendance/configuration */
export interface LC_UpdateAttendanceConfigurationConfigIn {
  offlineGeoLocationRequired: boolean;
  lunchBreakMarks: boolean | null;
  validateGeoLocation: boolean;
  disableGeoMark: boolean | null;
  notifyGeoInconsistencyApi: boolean | null;
  enableMarkRejection: boolean | null;
  markRejectionDeadlineHours: number | null;
  enableTodayMarkModification: boolean | null;
  eventDefinitionMethod: string | null;
  shiftChangeEntryEventId: number | null;
  shiftChangeExitEventId: number | null;
  lunchBreakToleranceMinutes: number;
  lunchBreakEntryEventId: number | null;
  lunchBreakExitEventId: number | null;
  monthlyCut: boolean;
  sendConceptDetail: boolean;
  holidayAsWorkday: boolean;
  holidayAsWorkdayArt38: boolean;
  halfDayAbsenteeismType: number;
  entryMarkToleranceMinutes: number | null;
  exitMarkToleranceMinutes: number | null;
  duplicateRecordToleranceMinutes: number;
  absenceByApplication: string | null;
  notifyEmployeeDelay: boolean;
  notifyManagerDelay: boolean;
  notifyAccessControl: boolean;
  notifyManagementInsistence: boolean | null;
  managementInsistenceEmail: string | null;
  notifyDisconnectedDevice: boolean | null;
  disconnectedDeviceEmail: string | null;
  incidentNotificationEmployeeId: number | null;
  notifyEntryDelay: boolean | null;
  entryDelayNotificationTolerance: string | null;
  notifyExitDelay: boolean | null;
  exitDelayNotificationTolerance: string | null;
  contractCompanyAbsence: boolean | null;
  bulkActionEmailAddress: string | null;
  dtAuditStartNotificationEmails: string | null;
  autoFillHolidays: boolean;
  autoFillVacations: boolean;
  autoFillSickLeave: boolean;
  autoFillSpecialPermissions: boolean;
  autoFillAdministrativePermissions: boolean;
  autoFillFractionPermissions: boolean;
  overtimeApprovalFlowEnabled: boolean | null;
  autoCompensationMode: string | null;
  cutToleranceDays: number | null;
  notifyExcessScheduleEmail: string | null;
}

/** Mark parameter item for PUT body */
export interface LC_UpdateAttendanceMarkParameterIn {
  attributeName: string | null;
  value: string | null;
}

/** Overtime approval flow role item for PUT body */
export interface LC_UpdateOvertimeApprovalFlowRoleIn {
  flowProfileId: number;
  roleIds: number[] | null;
}

/** Full PUT /attendance/configuration request body */
export interface LC_UpdateAttendanceConfigurationIn {
  configuration: LC_UpdateAttendanceConfigurationConfigIn;
  attendanceMarkParameters: LC_UpdateAttendanceMarkParameterIn[] | null;
  overtimeApprovalFlowRoles: LC_UpdateOvertimeApprovalFlowRoleIn[] | null;
}

/** PUT /attendance/configuration response (empty object = success) */
export type LC_UpdateAttendanceConfigurationOut = Record<string, never>;

// ─── Domain model ───────────────────────────────────────────────────────────

export interface AttendanceConfiguration {
  // General - Marcajes
  geoOfflineRequired: boolean;
  collationMarkings: boolean;
  geoWebMarking: boolean;
  disableGeoMarking: boolean;
  geoInconsistencyAlert: boolean;
  employeeMarkingApproval: boolean;
  approvalDeadlineHours: number | null;
  todayMarkingModification: boolean;

  // General - Eventos
  eventDefinitionMethod: string;
  entryEvent: number | null;
  exitEvent: number | null;
  collationTolerance: number | null;
  collationEntryEvent: number | null;
  collationExitEvent: number | null;

  // Cálculo - Cortes Mensuales
  multiCutsSameMonth: boolean;
  sendByDateConcept: boolean;
  groupClosure: boolean;

  // Cálculo - Condiciones
  holidaysAsWorkdays: boolean;
  art38IgnoreHolidays: boolean;
  calendarScheduleManagement: boolean;
  halfDayAbsenteeism: string;
  calculationByContractEnd: boolean;

  // Notificaciones - Tolerancia
  entryAbsenceMinutes: number | null;
  exitAbsenceMinutes: number | null;
  repeatedMarkingWait: number | null;
  absenceValidationControls: number[];

  // Notificaciones - Notificaciones
  notifyAbsenceEmployee: boolean;
  notifyAbsenceSupervisor: boolean;
  notifyNoTimeControl: boolean;
  notifyAbsenceAdmin: boolean;
  adminAbsenceEmail: string;
  notifyDisconnectedDevice: boolean;
  disconnectedDeviceEmail: string;
  markingIncidentRecipient: number | null;
  notifyEntryDelay: boolean;
  entryDelayToleranceMinutes: number | null;
  notifyExitDelay: boolean;
  exitDelayToleranceMinutes: number | null;
  contractCompanyAbsence: boolean;
  massAttendanceEmail: string;
  dtFiscalizationEmails: string;
  excessHoursEmail: string;

  // Validaciones - Gestor de Marcas
  autoFillHolidays: boolean;
  autoFillVacations: boolean;
  autoFillSickLeave: boolean;
  autoFillSpecialPermissions: boolean;
  autoFillAdminPermissions: boolean;
  autoFillFractionPermissions: boolean;

  // Validaciones - Horas Extras
  approvalFlow: boolean;
  reviewerProfiles: number[];
  approverProfiles: number[];

  // Validaciones - Pacto Compensación
  automaticMode: string;
  cutDaysSlack: number | null;

  // Metadata for update (flowProfileId per role profile code)
  reviewerFlowProfileId: number | null;
  approverFlowProfileId: number | null;
}
